import type { CameraCapturedPicture } from 'expo-camera';

export type CoverCropRect = {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
};

export type ViewSize = {
  width: number;
  height: number;
};

export const getCoverCropRect = (
  sourceWidth: number,
  sourceHeight: number,
  viewWidth: number,
  viewHeight: number
): CoverCropRect => {
  if (sourceWidth <= 0 || sourceHeight <= 0 || viewWidth <= 0 || viewHeight <= 0) {
    return { sx: 0, sy: 0, sw: sourceWidth, sh: sourceHeight };
  }

  const scale = Math.max(viewWidth / sourceWidth, viewHeight / sourceHeight);
  const sw = Math.min(sourceWidth, viewWidth / scale);
  const sh = Math.min(sourceHeight, viewHeight / scale);

  return {
    sx: (sourceWidth - sw) / 2,
    sy: (sourceHeight - sh) / 2,
    sw,
    sh,
  };
};

export const isWebPreviewMirrored = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  const videos = Array.from(document.querySelectorAll('video'));
  for (const video of videos) {
    const stream = video.srcObject;
    if (!(stream instanceof MediaStream)) {
      continue;
    }

    const facingMode = stream.getVideoTracks()[0]?.getSettings()?.facingMode;
    return !facingMode || facingMode === 'user';
  }

  return true;
};

const loadImage = (uri: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Não foi possível recortar a foto da câmera web.'));
    image.src = uri;
  });

export const alignWebCameraCapture = async (
  picture: CameraCapturedPicture,
  viewSize: ViewSize,
  quality = 0.7
): Promise<CameraCapturedPicture> => {
  if (typeof document === 'undefined') {
    return picture;
  }

  const sourceWidth = picture.width || 0;
  const sourceHeight = picture.height || 0;
  const crop = getCoverCropRect(sourceWidth, sourceHeight, viewSize.width, viewSize.height);
  const mirror = isWebPreviewMirrored();
  const needsCrop =
    crop.sx > 0.5 || crop.sy > 0.5 || Math.abs(crop.sw - sourceWidth) > 0.5 || Math.abs(crop.sh - sourceHeight) > 0.5;

  if (!mirror && !needsCrop) {
    return picture;
  }

  const image = await loadImage(picture.uri);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(crop.sw));
  canvas.height = Math.max(1, Math.round(crop.sh));
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    throw new Error('Canvas não disponível para alinhar a foto da câmera web.');
  }

  if (mirror) {
    context.setTransform(-1, 0, 0, 1, canvas.width, 0);
  }

  context.drawImage(
    image,
    crop.sx,
    crop.sy,
    crop.sw,
    crop.sh,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const uri = canvas.toDataURL('image/jpeg', quality);
  return {
    ...picture,
    uri,
    width: canvas.width,
    height: canvas.height,
    format: 'jpg',
  };
};
